import { PortableText } from '@portabletext/react'
import { urlFor } from '../../lib/sanity'

const tableCellComponents = {
  block: {
    normal: ({ children }) => (
      <p className="mb-2 last:mb-0 text-neutral-300 leading-relaxed">{children}</p>
    ),
  },
  marks: {
    strong: ({ children }) => (
      <strong className="font-bold text-neutral-100">{children}</strong>
    ),
    em: ({ children }) => (
      <em className="italic text-neon-yellow">{children}</em>
    ),
    code: ({ children }) => (
      <code className="bg-dark-800 text-neon-lime px-1.5 py-0.5 rounded text-sm font-mono border border-neon-lime/20">
        {children}
      </code>
    ),
    link: ({ children, value }) => (
      <a
        href={value?.href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-neon-pink hover:text-neon-purple underline decoration-neon-pink/30 hover:decoration-neon-purple transition-colors"
      >
        {children}
      </a>
    ),
  },
}

export const portableTextComponents = {
  block: {
    h1: ({ children }) => (
      <h1 className="text-4xl text-neon-lime mb-6 mt-8">
        {children}
      </h1>
    ),
    h2: ({ children }) => (
      <h2 className="text-3xl text-neon-orange mb-5 mt-8">
        {children}
      </h2>
    ),
    h3: ({ children }) => (
      <h3 className="text-2xl text-neon-pink mb-4 mt-6">
        {children}
      </h3>
    ),
    h4: ({ children }) => (
      <h4 className="text-xl text-neon-purple mb-3 mt-6">
        {children}
      </h4>
    ),
    normal: ({ children }) => (
      <p className="text-lg text-neutral-300 mb-6 leading-relaxed">
        {children}
      </p>
    ),
    blockquote: ({ children }) => (
      <blockquote className="border-l-4 border-neon-lime pl-6 py-2 my-6 italic text-neutral-300 bg-dark-800/50 rounded-r-lg">
        {children}
      </blockquote>
    ),
  },
  list: {
    bullet: ({ children }) => (
      <ul className="list-none space-y-3 mb-6 ml-0">
        {children}
      </ul>
    ),
    number: ({ children }) => (
      <ol className="list-none space-y-3 mb-6 ml-0 counter-reset-list">
        {children}
      </ol>
    ),
  },
  listItem: {
    bullet: ({ children }) => (
      <li className="flex items-start gap-3 text-lg text-neutral-300">
        <span className="text-neon-lime mt-1 flex-shrink-0">•</span>
        <span>{children}</span>
      </li>
    ),
    number: ({ children }) => (
      <li className="flex items-start gap-3 text-lg text-neutral-300">
        <span className="text-neon-orange font-bold flex-shrink-0">→</span>
        <span>{children}</span>
      </li>
    ),
  },
  marks: {
    strong: ({ children }) => (
      <strong className="font-bold text-neutral-100">{children}</strong>
    ),
    em: ({ children }) => (
      <em className="italic text-neon-yellow">{children}</em>
    ),
    code: ({ children }) => (
      <code className="bg-dark-800 text-neon-lime px-2 py-1 rounded text-sm font-mono border border-neon-lime/20">
        {children}
      </code>
    ),
    link: ({ children, value }) => (
      <a
        href={value?.href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-neon-pink hover:text-neon-purple underline decoration-neon-pink/30 hover:decoration-neon-purple transition-colors"
      >
        {children}
      </a>
    ),
  },
  types: {
    image: ({ value }) => {
      if (!value?.asset?._ref) return null

      return (
        <figure className="my-8">
          <img
            src={urlFor(value).width(800).url()}
            alt={value.alt || 'Blog post image'}
            className="w-full rounded-xl border border-neon-lime/20"
          />
          {value.caption && (
            <figcaption className="text-center text-sm text-neutral-500 mt-3">
              {value.caption}
            </figcaption>
          )}
        </figure>
      )
    },
    richTable: ({ value }) => {
      const { caption, header, rows = [] } = value || {}
      if (!Array.isArray(rows) || rows.length === 0) return null

      const headerCells = header?.cells || []

      return (
        <figure className="my-8 overflow-x-auto">
          <table className="w-full border-collapse rounded-xl overflow-hidden border border-neon-lime/20 text-left">
            {headerCells.length > 0 && (
              <thead className="bg-dark-800">
                <tr>
                  {headerCells.map((cell, i) => (
                    <th
                      key={i}
                      className="border border-neon-lime/20 px-4 py-3 text-neon-lime font-semibold align-top"
                    >
                      <PortableText
                        value={cell?.content || []}
                        components={tableCellComponents}
                      />
                    </th>
                  ))}
                </tr>
              </thead>
            )}
            <tbody>
              {rows.map((row, ri) => (
                <tr
                  key={ri}
                  className={ri % 2 === 0 ? 'bg-dark-900/40' : 'bg-dark-800/40'}
                >
                  {(row?.cells || []).map((cell, ci) => (
                    <td
                      key={ci}
                      className="border border-neon-lime/20 px-4 py-3 align-top text-neutral-300"
                    >
                      <PortableText
                        value={cell?.content || []}
                        components={tableCellComponents}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {caption && (
            <figcaption className="text-center text-sm text-neutral-500 mt-3">
              {caption}
            </figcaption>
          )}
        </figure>
      )
    },
  },
}
